import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { buildApiUrl } from '../../config/api'
import GenderIcon, { MaleIcon, FemaleIcon } from '../../components/common/GenderIcon'

const TournamentResults = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { token } = useAuth()
  const [event, setEvent] = useState(null)
  const [results, setResults] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('all')
  const [activeResultType, setActiveResultType] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [addStep, setAddStep] = useState('gender') // 'gender' or 'form'
  const [formData, setFormData] = useState({
    category: '',
    gender: '',
    team_id: '',
    position: 'winner'
  })
  const [teams, setTeams] = useState([])
  const [saving, setSaving] = useState(false)
  const [availableGenders, setAvailableGenders] = useState({ male: true, female: true })
  const [loadingGenders, setLoadingGenders] = useState(false)

  const API_BASE = buildApiUrl('/client-admin')

  useEffect(() => {
    loadEventDetails()
    loadResults()
  }, [eventId])

  // Load categories after event is loaded
  useEffect(() => {
    if (event) {
      loadCategories()
    }
  }, [event])

  const loadEventDetails = async () => {
    try {
      const response = await fetch(`${API_BASE}/events/${eventId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        setEvent(data.data || data.event)
      }
    } catch (error) {
      console.error('Error loading event:', error)
    }
  }

  const loadResults = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE}/events/${eventId}/results`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        setResults(data.data?.results || [])
      }
    } catch (error) {
      console.error('Error loading results:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadAvailableGenders = async () => {
    try {
      setLoadingGenders(true)
      const response = await fetch(`${API_BASE}/events/${eventId}/available-genders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        setAvailableGenders(data.data || { male: true, female: true })
      }
    } catch (error) {
      console.error('Error loading available genders:', error)
    } finally {
      setLoadingGenders(false)
    }
  }

  const loadParticipatingTeams = async (gender) => {
    try {
      const genderParam = gender ? `?gender=${gender}` : ''
      const response = await fetch(`${API_BASE}/events/${eventId}/participating-teams${genderParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        setTeams(data.data || [])
      }
    } catch (error) {
      console.error('Error loading teams:', error)
    }
  }


  const loadCategories = async () => {
    try {
      // First try to get categories from event's allowed_categories
      if (event?.allowed_categories && event.allowed_categories.length > 0) {
        const cats = event.allowed_categories.map(c => ({
          id: c.id,
          code: c.code,
          level_name: c.name,
          genders: c.genders || []
        }))
        setCategories(cats)
        return
      }

      // Fallback: Load from master categories
      const response = await fetch(`${API_BASE}/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        const cats = (data.data || []).map(c => ({
          id: c.id,
          code: c.code,
          level_name: c.name || c.level_name,
          genders: ['M', 'F']
        }))
        setCategories(cats)
      }
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const handleAddResult = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      // Convert position to medal type
      let medal = 'none'
      if (formData.position === 'winner' || formData.position === 'joint-winner') medal = 'gold'
      else if (formData.position === 'runner-up' || formData.position === 'joint-runner-up') medal = 'silver'
      else if (formData.position === '3rd-place' || formData.position === 'joint-3rd-place') medal = 'bronze'

      const response = await fetch(`${API_BASE}/events/${eventId}/results`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          team_id: formData.team_id,
          category: formData.category,
          gender: formData.gender,
          position: formData.position,
          medal: medal
        })
      })
      if (response.ok) {
        showNotification('Team result added successfully!', 'success')
        setShowAddModal(false)
        setFormData({
          category: '',
          gender: '',
          team_id: '',
          position: 'winner'
        })
        loadResults()
      } else {
        const error = await response.json()
        showNotification(error.message || 'Failed to add result', 'error')
      }
    } catch (error) {
      showNotification('Failed to add result', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Handle category change - reset gender and team
  const handleCategoryChange = (newCategory) => {
    setFormData({
      ...formData,
      category: newCategory,
      gender: '',
      team_id: ''
    })
  }

  // Handle gender change - load teams
  const handleGenderChange = (newGender) => {
    setFormData({
      ...formData,
      gender: newGender,
      team_id: ''
    })
    loadParticipatingTeams(newGender)
  }

  const handleDeleteResult = async (resultId) => {
    if (!confirm('Are you sure you want to delete this result?')) return
    try {
      const response = await fetch(`${API_BASE}/events/${eventId}/results/${resultId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        showNotification('Result deleted!', 'success')
        loadResults()
      }
    } catch (error) {
      showNotification('Failed to delete', 'error')
    }
  }

  const showNotification = (message, type) => {
    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      color: white;
      font-weight: 600;
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 10px;
      background: ${type === 'success' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)'};
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `
    notification.innerHTML = `<i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}"></i> ${message}`
    document.body.appendChild(notification)
    setTimeout(() => notification.remove(), 3000)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getMedalIcon = (medal) => {
    switch(medal) {
      case 'gold': return { icon: '🥇', color: '#fbbf24', bg: '#fef3c7' }
      case 'silver': return { icon: '🥈', color: '#9ca3af', bg: '#f3f4f6' }
      case 'bronze': return { icon: '🥉', color: '#cd7f32', bg: '#fef3c7' }
      case 'none': return { icon: '—', color: '#9ca3af', bg: '#f3f4f6' }
      default: return { icon: '—', color: '#6b7280', bg: '#f9fafb' }
    }
  }

  // Get unique categories from results
  const getResultCategories = () => {
    const cats = [...new Set(results.map(r => r.category || r.level_name))]
    return cats.filter(c => c)
  }

  // Filter results by category and result type
  const getFilteredResults = () => {
    let filtered = results

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(r => (r.category || r.level_name) === activeCategory)
    }

    // Filter by result type
    if (activeResultType !== 'all') {
      filtered = filtered.filter(r => r.position === activeResultType)
    }

    return filtered
  }

  // Group results by category and gender
  const getGroupedResults = () => {
    const filtered = getFilteredResults()
    const grouped = {}

    filtered.forEach(result => {
      const cat = result.category || result.level_name || 'General'
      const gender = result.gender === 'M' ? 'Male' : 'Female'

      if (!grouped[cat]) {
        grouped[cat] = { Male: [], Female: [] }
      }
      grouped[cat][gender].push(result)
    })

    // Sort by position within each group
    const positionOrder = { 'winner': 1, 'joint-winner': 1, 'runner-up': 2, 'joint-runner-up': 2, '3rd-place': 3, 'joint-3rd-place': 3 }
    Object.keys(grouped).forEach(cat => {
      grouped[cat].Male.sort((a, b) => (positionOrder[a.position] || 99) - (positionOrder[b.position] || 99))
      grouped[cat].Female.sort((a, b) => (positionOrder[a.position] || 99) - (positionOrder[b.position] || 99))
    })

    return grouped
  }

  // Get available genders for selected category
  const getAvailableGenders = () => {
    if (!formData.category) return []

    const selectedCategory = categories.find(c => c.level_name === formData.category)
    if (!selectedCategory || !selectedCategory.genders) return ['M', 'F']

    return selectedCategory.genders
  }

  const renderResultsTable = (results, title, genderIcon) => {
    if (results.length === 0) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          color: '#9ca3af',
          background: '#f9fafb',
          borderRadius: '8px',
          margin: '0.5rem 0'
        }}>
          <GenderIcon gender={genderIcon} style={{ fontSize: '2rem', marginBottom: '0.5rem', display: 'block' }} />
          No {title} results yet
        </div>
      )
    }

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
        <thead style={{ background: 'linear-gradient(to right, #c00f15, #a00d12, #c00f15)' }}>
          <tr style={{ background: 'transparent' }}>
            <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent', width: '12%' }}>Position</th>
            <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent', width: '12%' }}>Medal</th>
            <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent', width: '40%' }}>Team/Association</th>
            <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent', width: '20%' }}>State</th>
            <th style={{ padding: '12px 16px', color: '#fff', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent', width: '16%' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {results.map((result, index) => {
            const medal = getMedalIcon(result.medal)
            return (
              <tr key={result.id} style={{
                borderBottom: '1px solid #e5e7eb',
                background: index % 2 === 0 ? 'white' : '#fafbfc'
              }}>
                <td style={{ padding: '12px 16px', fontWeight: '700', fontSize: '1rem', color: '#1f2937' }}>
                  {result.position_text || result.position || '-'}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    background: medal.bg,
                    borderRadius: '20px',
                    fontSize: '1.2rem'
                  }}>
                    {medal.icon}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1f2937', fontSize: '0.95rem' }}>
                  {result.team_name || '-'}
                </td>
                <td style={{ padding: '12px 16px', color: '#4b5563' }}>
                  {result.state_name || '-'}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <button
                    onClick={() => handleDeleteResult(result.id)}
                    style={{
                      padding: '8px 14px',
                      background: '#fee2e2',
                      color: '#ef4444',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}
                  >
                    <i className="fas fa-trash"></i> Delete
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  const groupedResults = getGroupedResults()

  return (
    <div style={{ padding: '1.5rem 2rem', width: '100%', minHeight: '100vh', background: '#f3f4f6' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 50%, #c00f15 100%)',
        padding: '1.5rem 2rem',
        color: 'white',
        borderRadius: '12px',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <button
            onClick={() => {
              setAddStep('gender')
              setFormData({ category: '', gender: '', team_id: '', position: 'winner' })
              setShowAddModal(true)
              loadAvailableGenders()
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '2px solid rgba(255,255,255,0.4)',
              color: '#ffffff',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            <i className="fas fa-plus"></i>
            Add Result
          </button>
          <button
            onClick={() => navigate('/admin/events')}
            style={{
              background: '#ffffff',
              border: 'none',
              color: '#c00f15',
              padding: '8px 18px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap'
            }}
          >
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
        </div>
        <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem', fontWeight: '700', color: '#ffffff' }}>
          <i className="fas fa-trophy" style={{ marginRight: '12px', color: '#fbbf24' }}></i>
          Tournament Results
        </h1>
        <p style={{ margin: 0, fontSize: '1rem', color: '#ffffff' }}>
          {event?.event_name || 'Loading...'}
        </p>
        {event && (
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>
            <i className="fas fa-calendar" style={{ marginRight: '8px' }}></i>
            {formatDate(event.event_start_date)} - {formatDate(event.event_end_date)}
            <span style={{ margin: '0 15px', color: 'rgba(255,255,255,0.6)' }}>|</span>
            <i className="fas fa-map-marker-alt" style={{ marginRight: '8px' }}></i>
            {event.event_venue}
          </p>
        )}
      </div>

      {/* Result Type Tabs */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'Participating Teams', icon: <i className="fas fa-users"></i> },
            { key: 'winner', label: 'Winner', icon: <span style={{ fontSize: '1rem' }}>🥇</span> },
            { key: 'joint-winner', label: 'Joint Winner', icon: <span style={{ fontSize: '1rem' }}>🥇</span> },
            { key: 'runner-up', label: 'Runner Up', icon: <span style={{ fontSize: '1rem' }}>🥈</span> },
            { key: 'joint-runner-up', label: 'Joint Runner Up', icon: <span style={{ fontSize: '1rem' }}>🥈</span> },
            { key: '3rd-place', label: '3rd Place', icon: <span style={{ fontSize: '1rem' }}>🥉</span> },
            { key: 'joint-3rd-place', label: 'Joint 3rd Place', icon: <span style={{ fontSize: '1rem' }}>🥉</span> },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveResultType(tab.key)}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.85rem',
                background: activeResultType === tab.key ? 'linear-gradient(135deg, #c00f15, #a00d12)' : '#f3f4f6',
                color: activeResultType === tab.key ? 'white' : '#4b5563',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category Tabs */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveCategory('all')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.9rem',
              background: activeCategory === 'all' ? 'linear-gradient(135deg, #c00f15, #a00d12)' : '#f3f4f6',
              color: activeCategory === 'all' ? 'white' : '#4b5563',
              transition: 'all 0.2s'
            }}
          >
            All Categories
          </button>
          {getResultCategories().map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.9rem',
                background: activeCategory === cat ? 'linear-gradient(135deg, #c00f15, #a00d12)' : '#f3f4f6',
                color: activeCategory === cat ? 'white' : '#4b5563',
                transition: 'all 0.2s'
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Results Content */}
      {loading ? (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#3b82f6' }}></i>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading results...</p>
        </div>
      ) : Object.keys(groupedResults).length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <i className="fas fa-trophy" style={{ fontSize: '4rem', color: '#d1d5db', marginBottom: '1rem' }}></i>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>No Results Yet</h3>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Click "Add Result" to start adding tournament results
          </p>
        </div>
      ) : (
        Object.entries(groupedResults).map(([category, genderResults]) => (
          <div key={category} style={{
            background: 'white',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            {/* Category Header */}
            <div style={{
              background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-layer-group" style={{ color: '#022a5e' }}></i>
                {category}
                <span style={{
                  background: 'linear-gradient(135deg, #022a5e, #053e88)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  marginLeft: 'auto'
                }}>
                  {genderResults.Male.length + genderResults.Female.length} Results
                </span>
              </h2>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {/* Male Results - Only show if there are male results */}
              {genderResults.Male.length > 0 && (
                <div style={{ marginBottom: genderResults.Female.length > 0 ? '2rem' : '0' }}>
                  <h3 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1rem',
                    color: '#1d4ed8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #dbeafe'
                  }}>
                    <MaleIcon />
                    Male Results
                    <span style={{
                      background: '#dbeafe',
                      color: '#1d4ed8',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '0.8rem'
                    }}>
                      {genderResults.Male.length}
                    </span>
                  </h3>
                  {renderResultsTable(genderResults.Male, 'Male', 'mars')}
                </div>
              )}

              {/* Female Results - Only show if there are female results */}
              {genderResults.Female.length > 0 && (
                <div>
                  <h3 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1rem',
                    color: '#db2777',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid #fce7f3'
                  }}>
                    <FemaleIcon />
                    Female Results
                    <span style={{
                      background: '#fce7f3',
                      color: '#db2777',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '0.8rem'
                    }}>
                      {genderResults.Female.length}
                    </span>
                  </h3>
                  {renderResultsTable(genderResults.Female, 'Female', 'venus')}
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {/* Add Result Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }} onClick={() => setShowAddModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '650px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 60px rgba(0,0,0,0.35)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 50%, #c00f15 100%)',
              padding: '1.5rem',
              color: '#ffffff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', color: '#ffffff' }}>
                  <i className="fas fa-trophy"></i>
                  {addStep === 'gender' ? 'Select Gender' : `Add Result - ${formData.gender === 'M' ? 'Male' : 'Female'}`}
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            {/* Step 1: Gender Selection */}
            {addStep === 'gender' && (
              <div style={{ padding: '2rem 1.5rem' }}>
                <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '1.5rem', fontSize: '1rem' }}>
                  Select gender category for the result
                </p>
                {loadingGenders ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', marginBottom: '0.5rem', display: 'block' }}></i>
                    Loading...
                  </div>
                ) : !availableGenders.male && !availableGenders.female ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
                    <i className="fas fa-exclamation-circle" style={{ fontSize: '1.5rem', marginBottom: '0.5rem', display: 'block' }}></i>
                    No teams with approved players found for this event
                  </div>
                ) : (
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  {availableGenders.male && (
                  <button
                    onClick={() => {
                      setFormData({ ...formData, gender: 'M', category: '', team_id: '', position: 'winner' })
                      loadParticipatingTeams('M')
                      setAddStep('form')
                    }}
                    style={{
                      flex: 1,
                      maxWidth: '150px',
                      padding: '1.2rem 1rem',
                      background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: '0 4px 12px rgba(29, 78, 216, 0.3)'
                    }}
                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(29, 78, 216, 0.4)' }}
                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(29, 78, 216, 0.3)' }}
                  >
                    <MaleIcon style={{ fontSize: '1.8rem' }} />
                    <span style={{ fontSize: '0.95rem', fontWeight: '700' }}>Male</span>
                  </button>
                  )}
                  {availableGenders.female && (
                  <button
                    onClick={() => {
                      setFormData({ ...formData, gender: 'F', category: '', team_id: '', position: 'winner' })
                      loadParticipatingTeams('F')
                      setAddStep('form')
                    }}
                    style={{
                      flex: 1,
                      maxWidth: '150px',
                      padding: '1.2rem 1rem',
                      background: 'linear-gradient(135deg, #db2777, #ec4899)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: '0 4px 12px rgba(219, 39, 119, 0.3)'
                    }}
                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(219, 39, 119, 0.4)' }}
                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(219, 39, 119, 0.3)' }}
                  >
                    <FemaleIcon style={{ fontSize: '1.8rem' }} />
                    <span style={{ fontSize: '0.95rem', fontWeight: '700' }}>Female</span>
                  </button>
                  )}
                </div>
                )}
                <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    style={{
                      padding: '10px 24px',
                      background: '#f3f4f6',
                      color: '#4b5563',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Result Form */}
            {addStep === 'form' && (
              <form onSubmit={handleAddResult} style={{ padding: '1.5rem' }}>
                {/* Selected Gender Indicator */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '1rem',
                  padding: '10px 14px',
                  background: formData.gender === 'M' ? '#dbeafe' : '#fce7f3',
                  borderRadius: '8px',
                  color: formData.gender === 'M' ? '#1d4ed8' : '#db2777',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}>
                  <GenderIcon gender={formData.gender} />
                  {formData.gender === 'M' ? 'Male' : 'Female'} Category
                  <button
                    type="button"
                    onClick={() => setAddStep('gender')}
                    style={{
                      marginLeft: 'auto',
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      textDecoration: 'underline'
                    }}
                  >
                    Change
                  </button>
                </div>

                {/* Category Selection */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={e => {
                      setFormData({ ...formData, category: e.target.value, team_id: '' })
                      loadParticipatingTeams(formData.gender)
                    }}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.95rem'
                    }}
                  >
                    <option value="">Select Category</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.level_name}>
                        {c.level_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Team Selection */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                    Team/Association *
                  </label>
                  <select
                    value={formData.team_id}
                    onChange={e => setFormData({...formData, team_id: e.target.value})}
                    required
                    disabled={!formData.category}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      opacity: !formData.category ? 0.6 : 1,
                      cursor: !formData.category ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">Select Team</option>
                    {teams.filter(t => {
                      // Hide teams that already have a result for this gender + category
                      return !results.some(r =>
                        String(r.team_id) === String(t.id) &&
                        r.gender === formData.gender &&
                        r.category === formData.category
                      )
                    }).map(t => (
                      <option key={t.id} value={t.id}>
                        {t.association_name} ({t.total_members} members)
                      </option>
                    ))}
                  </select>
                  {!formData.category && (
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                      Select category first
                    </p>
                  )}
                </div>

                {/* Position Selection */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                    Result *
                  </label>
                  {(() => {
                    // Count existing results for same gender + category
                    const sameGenderCatResults = results.filter(r =>
                      r.gender === formData.gender && r.category === formData.category
                    )
                    const positionCounts = {}
                    sameGenderCatResults.forEach(r => {
                      positionCounts[r.position] = (positionCounts[r.position] || 0) + 1
                    })

                    // Max limits: single positions = 1, joint positions = 2
                    const allPositions = [
                      { value: 'winner', label: 'Winner', max: 1 },
                      { value: 'joint-winner', label: 'Joint Winner', max: 2 },
                      { value: 'runner-up', label: 'Runner Up', max: 1 },
                      { value: 'joint-runner-up', label: 'Joint Runner Up', max: 2 },
                      { value: '3rd-place', label: '3rd Place', max: 1 },
                      { value: 'joint-3rd-place', label: 'Joint 3rd Place', max: 2 },
                    ]
                    const availablePositions = allPositions.filter(p =>
                      (positionCounts[p.value] || 0) < p.max
                    )

                    // Reset position if current selection is no longer available
                    if (formData.position && !availablePositions.find(p => p.value === formData.position)) {
                      if (availablePositions.length > 0) {
                        setTimeout(() => setFormData(prev => ({ ...prev, position: availablePositions[0].value })), 0)
                      }
                    }

                    return (
                      <>
                        <select
                          value={formData.position}
                          onChange={e => setFormData({...formData, position: e.target.value})}
                          required
                          disabled={!formData.team_id || availablePositions.length === 0}
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            opacity: (!formData.team_id || availablePositions.length === 0) ? 0.6 : 1,
                            cursor: (!formData.team_id || availablePositions.length === 0) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {availablePositions.length === 0 ? (
                            <option value="">All positions filled</option>
                          ) : (
                            availablePositions.map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))
                          )}
                        </select>
                        {!formData.team_id && (
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                            Select team first
                          </p>
                        )}
                        {formData.team_id && availablePositions.length === 0 && (
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                            All positions are filled for this gender &amp; category
                          </p>
                        )}
                      </>
                    )
                  })()}
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => setAddStep('gender')}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#f3f4f6',
                      color: '#4b5563',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.95rem'
                    }}
                  >
                    <i className="fas fa-arrow-left" style={{ marginRight: '6px' }}></i>
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      opacity: saving ? 0.7 : 1
                    }}
                  >
                    {saving ? (
                      <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                    ) : (
                      <><i className="fas fa-check"></i> Add Result</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default TournamentResults
